import { defineStore } from 'pinia';
import { callMethod } from '@/api/client';
import { isMainSite, mainSiteUrl } from '@/utils/site';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,            // { email, full_name, region }
    checked: false,        // whether checkSession() has run at least once
    loading: false,
    redirecting: false,    // true while we're navigating to a region site
    redirectMessage: '',   // text shown during the redirect overlay
  }),

  getters: {
    isAuthenticated: (state) => !!state.user,
    isAdmin: (state) => !!state.user?.is_admin,
    // System User or Company Admin — both get the Permission page
    canManageUsers: (state) => !!state.user?.can_manage_users,
    // hasModule('listings') — true when unrestricted (modules missing) or
    // the module key is in the user's allowed list.
    hasModule: (state) => (key) => {
      const mods = state.user?.modules;
      return !Array.isArray(mods) || mods.includes(key);
    },
    // hasPermission('credentials', 'write') — action-level check within a
    // module. Mirrors the backend's "no permissions map = unrestricted"
    // default (utils/module_permissions.py:_full_permissions).
    hasPermission: (state) => (moduleKey, action) => {
      const perms = state.user?.permissions;
      if (!perms) return true;
      const actions = perms[moduleKey];
      return Array.isArray(actions) && actions.includes(action);
    },
  },

  actions: {
    async checkSession() {
      this.loading = true;
      try {
        const data = await callMethod(
          'marketplace_connector.api.auth.whoami',
          {},
          { method: 'GET' }
        );
        this.user = data?.user || null;
      } catch {
        this.user = null;
      } finally {
        this.checked = true;
        this.loading = false;
      }
    },

    /**
     * REGION-SITE login. Hits the local /login endpoint which authenticates
     * the user against this region's Frappe DB and sets the mc_session cookie.
     * After this resolves, /dashboard is reachable.
     */
    async loginNative(email, password) {
      this.loading = true;
      try {
        const result = await callMethod('marketplace_connector.api.auth.login', {
          email,
          password,
        });
        // The login endpoint returns top-level fields (email, region,
        // full_name, token, home_page) — NOT a nested `user` object like
        // whoami does. We have to construct the user shape ourselves so
        // isAuthenticated flips true and the router guard lets the
        // immediate router.push('/dashboard') through. Without this the
        // seller gets bounced back to /login by the guard (user=null)
        // and is stuck on a spinning button until they refresh.
        //
        // Backend sometimes returns full_name="Guest" because the
        // session is dropped right after authentication — fall back to
        // whoami in that case so the dashboard greets the actual user.
        if (result?.email) {
          this.user = {
            email: result.email,
            full_name: result.full_name && result.full_name !== 'Guest'
              ? result.full_name
              : '',
            region: result.region || '',
            is_admin: !!result.is_admin,
            role: result.role || 'admin',
            can_manage_users: !!result.can_manage_users,
            // Missing/empty → treat as unrestricted; whoami refresh will
            // deliver the authoritative list anyway.
            modules: Array.isArray(result.modules) ? result.modules : null,
            permissions: result.permissions || null,
          };
          this.checked = true;
          if (!this.user.full_name) {
            // Best effort: refresh from whoami so the greeting shows the
            // real name. Don't block login on it — fire-and-forget.
            this.checkSession().catch(() => {});
          }
        } else {
          // No usable identity in the response — fall back to whoami so
          // the cookie the backend just set is at least reflected in
          // the store.
          await this.checkSession();
        }
        return result;
      } finally {
        this.loading = false;
      }
    },

    /**
     * MAIN-SITE login forwarder. The user picked a region from the dropdown;
     * the main site proxies the credential check to that region. We get back
     * a JWT in the JSON body; we then redirect the browser to the region URL,
     * carrying the token via a one-shot URL fragment so the region's SPA can
     * stash it in localStorage / cookie before /dashboard loads.
     */
    async loginViaMain(email, password, region) {
      this.loading = true;
      try {
        return await callMethod(
          'marketplace_connector.api.auth.login_via_main',
          { email, password, region },
        );
      } finally {
        this.loading = false;
      }
    },

    async logout() {
      this.redirectMessage = 'Signing you out...';
      this.redirecting = true;
      try {
        await callMethod('marketplace_connector.api.auth.logout', {});
      } catch {
        // ignore - we're redirecting away anyway
      }
      // Clear mc_session cookie on this region domain (browser-side; server-side
      // it just expires after TTL anyway)
      document.cookie = 'mc_session=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
      this.user = null;
      this.checked = false;
      // If we're on a region site, send the user back to the main marketing/login site.
      window.location.href = isMainSite() ? '/' : `${mainSiteUrl()}/`;
    },

    /**
     * Hands the browser off to a region site after a successful main-site
     * signup or login. We append the JWT in the URL fragment (#mc_token=...)
     * so the receiving SPA can pluck it out and set its own mc_session cookie.
     * Fragments are NOT sent to the server, so the token never appears in
     * server logs en route.
     */
    redirectToRegion(regionUrl, token, region, nextPath = null) {
      this.redirectMessage = region
        ? `Connecting to your ${region} region...`
        : 'Redirecting...';
      this.redirecting = true;
      // Compose final URL: <region>/<next or /dashboard>#mc_token=...
      // `nextPath` may arrive as a full URL (route guards encode
      // `window.location.origin + path` so the main site can verify which
      // region the user came from). Strip the origin defensively so we
      // don't end up with `http://eu...http://eu.../api-access`.
      let path = nextPath || '/dashboard';
      try {
        if (/^https?:\/\//i.test(path)) {
          const parsed = new URL(path);
          path = parsed.pathname + parsed.search + parsed.hash;
        }
      } catch {
        // not a parseable URL; fall through with the raw value
      }
      if (!path.startsWith('/')) path = '/' + path;
      const url = `${regionUrl.replace(/\/+$/, '')}${path}#mc_token=${encodeURIComponent(token)}`;
      setTimeout(() => {
        window.location.href = url;
      }, 50);
    },
  },
});
